export type OwnerLeaseOccupancySource = {
  id?: string | null;
  unitId?: string | null;
  unitNumber?: string | null;
  status?: string | null;
};

export type OwnerUnitOccupancySource = {
  activeLeaseId?: string | null;
  availabilityStatus?: string | null;
  leaseOccupancyStatus?: string | null;
  occupancyStatus?: string | null;
  status?: string | null;
  isOccupied?: boolean | null;
  lease?: {
    id?: string | null;
    leaseId?: string | null;
    status?: string | null;
  } | null;
};

export type OwnerPropertyOccupancySource = {
  id?: string | null;
  totalUnits?: number | string | null;
  numberOfUnits?: number | string | null;
  actualUnits?: number | string | null;
  unitCount?: number | string | null;
  unitsCount?: number | string | null;
  occupiedUnits?: number | string | null;
  occupiedUnitCount?: number | string | null;
  vacantUnits?: number | string | null;
  units?: OwnerUnitOccupancySource[] | null;
};

export type OwnerOccupancySummary = {
  totalUnits: number;
  occupiedUnits: number;
  vacantUnits: number;
  occupancyRate: number;
};

const OCCUPYING_LEASE_STATUSES = new Set(['ACTIVE', 'NOTICE_GIVEN']);

export function ownerPropertyOccupancySummary(
  property: OwnerPropertyOccupancySource,
  leases: OwnerLeaseOccupancySource[] = [],
): OwnerOccupancySummary {
  const activeLeaseUnits = ownerActiveLeaseUnitCount(leases);
  const explicitOccupiedUnits = firstPositiveNumber(property.occupiedUnits, property.occupiedUnitCount);
  const totalUnits = ownerPropertyTotalUnits(property, leases, activeLeaseUnits, explicitOccupiedUnits);
  const occupiedUnits = ownerPropertyOccupiedUnits(property, leases, totalUnits, activeLeaseUnits);
  const vacantUnits = Math.max(0, totalUnits - occupiedUnits);

  return {
    totalUnits,
    occupiedUnits,
    vacantUnits,
    occupancyRate: totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0,
  };
}

export function ownerIsOccupyingLease(status: string | null | undefined): boolean {
  return OCCUPYING_LEASE_STATUSES.has(normalizeStatus(status));
}

export function ownerPropertyHasExplicitUnitTotal(property: OwnerPropertyOccupancySource): boolean {
  return firstPositiveNumber(
    property.totalUnits,
    property.actualUnits,
    property.numberOfUnits,
    property.unitCount,
    property.unitsCount,
  ) !== null || (Array.isArray(property.units) && property.units.length > 0);
}

function ownerPropertyTotalUnits(
  property: OwnerPropertyOccupancySource,
  leases: OwnerLeaseOccupancySource[],
  activeLeaseUnits: number,
  explicitOccupiedUnits: number | null,
): number {
  const explicit = firstPositiveNumber(
    property.totalUnits,
    property.actualUnits,
    property.numberOfUnits,
    property.unitCount,
    property.unitsCount,
  );
  if (explicit !== null) return explicit;

  const embeddedUnitCount = Array.isArray(property.units) ? property.units.length : 0;
  const leasedUnitCount = ownerLeaseUnitCount(leases);
  return Math.max(embeddedUnitCount, leasedUnitCount, activeLeaseUnits, explicitOccupiedUnits ?? 0, 0);
}

function ownerPropertyOccupiedUnits(
  property: OwnerPropertyOccupancySource,
  leases: OwnerLeaseOccupancySource[],
  totalUnits: number,
  activeLeaseUnits: number,
): number {
  const explicitOccupiedUnits = firstPositiveNumber(property.occupiedUnits, property.occupiedUnitCount);
  if (explicitOccupiedUnits !== null) {
    return clampUnitCount(explicitOccupiedUnits, totalUnits);
  }

  const explicitVacantUnits = firstNonNegativeNumber(property.vacantUnits);
  if (explicitVacantUnits !== null && totalUnits > 0) {
    return clampUnitCount(totalUnits - explicitVacantUnits, totalUnits);
  }

  const occupiedEmbeddedUnits = countOccupiedEmbeddedUnits(property.units);
  if (occupiedEmbeddedUnits !== null) {
    return clampUnitCount(occupiedEmbeddedUnits, totalUnits);
  }

  return clampUnitCount(activeLeaseUnits, totalUnits);
}

function countOccupiedEmbeddedUnits(units: OwnerUnitOccupancySource[] | null | undefined): number | null {
  if (!Array.isArray(units)) return null;

  let hasOccupancySignal = false;
  const occupiedUnits = units.filter((unit) => {
    if (typeof unit.isOccupied === 'boolean') {
      hasOccupancySignal = true;
      return unit.isOccupied;
    }

    if (Boolean(unit.activeLeaseId)) {
      hasOccupancySignal = true;
      return true;
    }

    if (unit.lease?.status) {
      hasOccupancySignal = true;
      return ownerIsOccupyingLease(unit.lease.status);
    }

    const status = normalizeStatus(unit.leaseOccupancyStatus ?? unit.occupancyStatus ?? unit.availabilityStatus ?? unit.status);
    if (!status) return false;

    hasOccupancySignal = true;
    return status.includes('OCCUPIED') || status.includes('LEASED') || status.includes('ACTIVE');
  }).length;

  return hasOccupancySignal ? occupiedUnits : null;
}

function ownerActiveLeaseUnitCount(leases: OwnerLeaseOccupancySource[]): number {
  return uniqueLeaseUnitKeys(leases.filter((lease) => ownerIsOccupyingLease(lease.status))).size;
}

function ownerLeaseUnitCount(leases: OwnerLeaseOccupancySource[]): number {
  return uniqueLeaseUnitKeys(leases).size;
}

function uniqueLeaseUnitKeys(leases: OwnerLeaseOccupancySource[]): Set<string> {
  return new Set(
    leases
      .map((lease) => String(lease.unitId ?? lease.unitNumber ?? lease.id ?? '').trim())
      .filter(Boolean),
  );
}

function firstPositiveNumber(...values: Array<number | string | null | undefined>): number | null {
  for (const value of values) {
    const parsed = numberValue(value);
    if (parsed > 0) return parsed;
  }
  return null;
}

function firstNonNegativeNumber(...values: Array<number | string | null | undefined>): number | null {
  for (const value of values) {
    const parsed = numberValue(value);
    if (parsed >= 0) return parsed;
  }
  return null;
}

function clampUnitCount(value: number, totalUnits: number): number {
  const normalized = Math.max(0, value);
  return totalUnits > 0 ? Math.min(normalized, totalUnits) : normalized;
}

function numberValue(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') return Number.NaN;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function normalizeStatus(status: string | null | undefined): string {
  return String(status ?? '').trim().toUpperCase();
}
